import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceService, locationService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Camera,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  LogIn,
  LogOut,
  RefreshCw,
  Calendar as CalendarIcon,
  Loader2,
  Scan,
  Navigation,
  Shield,
  Edit2,
  Search,
  Users,
} from 'lucide-react';
import { employeeService } from '../services/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function AttendancePage() {
  const { user, isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [livenessStatus, setLivenessStatus] = useState('idle');
  const [livenessScore, setLivenessScore] = useState(null);
  const [livenessChecking, setLivenessChecking] = useState(false);
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [cameraError, setCameraError] = useState('');
  const [cameraStream, setCameraStream] = useState(null);
  const [attendanceType, setAttendanceType] = useState('office');
  const [notes, setNotes] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [locations, setLocations] = useState([]);

  // HR Adjustment states
  const [allAttendance, setAllAttendance] = useState([]);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    check_in_time: '',
    check_out_time: '',
    notes: ''
  });
  const [selectedEmpId, setSelectedEmpId] = useState('personal');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const geolocationWatchRef = useRef(null);
  const geolocationTimeoutRef = useRef(null);

  const resetLivenessState = useCallback(() => {
    setLivenessStatus('idle');
    setLivenessScore(null);
  }, []);

  const cancelLivenessCheck = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch (error) {
        console.error('Failed to stop liveness recorder:', error);
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setLivenessChecking(false);
  }, []);

  const clearGeolocationWatch = useCallback(() => {
    if (geolocationWatchRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(geolocationWatchRef.current);
      geolocationWatchRef.current = null;
    }
    if (geolocationTimeoutRef.current) {
      clearTimeout(geolocationTimeoutRef.current);
      geolocationTimeoutRef.current = null;
    }
  }, []);

  const stopCamera = useCallback((reason = 'manual') => {
    cancelLivenessCheck();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraStream(null);
    setShowCamera(false);
    setCameraStatus('idle');
  }, [cancelLivenessCheck]);

  const loadAttendanceData = useCallback(async () => {
    try {
      let history;
      if ((isAdmin() || isManager()) && selectedEmpId && selectedEmpId !== 'personal') {
        const start_date = format(new Date(selectedYear, selectedMonth - 1, 1), 'yyyy-MM-dd');
        const end_date = format(new Date(selectedYear, selectedMonth, 0), 'yyyy-MM-dd');
        history = await attendanceService.getLogs({
          employee_id: selectedEmpId,
          start_date,
          end_date
        });
      } else {
        history = await attendanceService.getMyAttendance(selectedMonth, selectedYear);
      }
      setAttendanceHistory(history);

      // Find today's attendance (only if personal view)
      if (selectedEmpId === 'personal') {
        const today = format(new Date(), 'yyyy-MM-dd');
        const todayAttendance = history.find(a => a.date === today);
        setTodayStatus(todayAttendance);
      }

      if ((isAdmin() || isManager()) && employees.length === 0) {
        const emps = await employeeService.list();
        setEmployees(emps.filter(e => e.is_active));
      }
    } catch (error) {
      console.error('Failed to load attendance:', error);
    }
  }, [selectedMonth, selectedYear, selectedEmpId, isAdmin, isManager, employees.length]);

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationService.list();
      setLocations(locs);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  useEffect(() => {
    loadAttendanceData();
  }, [loadAttendanceData]);

  useEffect(() => {
    loadLocations();
    return () => {
      clearGeolocationWatch();
      stopCamera('cleanup');
    };
  }, [loadLocations, stopCamera, clearGeolocationWatch]);

  useEffect(() => {
    resetLivenessState();
  }, [attendanceType, resetLivenessState]);

  const validateCurrentLocation = useCallback(async (loc) => {
    if (!user?.employee_id) {
      return;
    }

    try {
      const validation = await attendanceService.validateGeofence(
        user.employee_id,
        loc.latitude,
        loc.longitude
      );
      setGeofenceStatus(validation);
    } catch (error) {
      console.error('Geofence validation failed:', error);
    }
  }, [user]);

  const getCurrentLocation = useCallback(() => {
    setLocationError(null);
    setGeofenceStatus(null);
    setLocationLoading(true);
    clearGeolocationWatch();

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLocationLoading(false);
      return;
    }

    let bestReading = null;

    const finalizeLocation = async (position) => {
      clearGeolocationWatch();
      setLocationLoading(false);

      if (!position) {
        setLocationError('Unable to get a reliable GPS fix');
        toast.error('Unable to get a reliable GPS fix');
        return;
      }

      const loc = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };
      setCurrentLocation(loc);

      if (loc.accuracy > 500) {
        toast.error(`Location accuracy is too low (${Math.round(loc.accuracy)}m). Move outdoors or enable precise location.`);
        setLocationError('Location accuracy is too low. Enable precise location and try again.');
        return;
      }

      if (loc.accuracy > 100) {
        toast.error(`Location accuracy is weak (${Math.round(loc.accuracy)}m). Results may be inaccurate.`);
      }

      await validateCurrentLocation(loc);
    };

    geolocationWatchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
          bestReading = position;
        }

        if (position.coords.accuracy <= 50) {
          finalizeLocation(position);
        }
      },
      (error) => {
        clearGeolocationWatch();
        setLocationLoading(false);
        setLocationError(error.message);
        toast.error('Failed to get location: ' + error.message);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );

    geolocationTimeoutRef.current = setTimeout(() => {
      finalizeLocation(bestReading);
    }, 8000);
  }, [clearGeolocationWatch, validateCurrentLocation]);

  const startCamera = async () => {
    try {
      setCameraError('');
      setCameraStatus('requesting');
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      streamRef.current = stream;
      setCameraStream(stream);
      setCameraStatus('streaming');
      getCurrentLocation();
    } catch (error) {
      setCameraStatus('error');
      setCameraError(error?.message || 'Failed to access camera');
      toast.error('Failed to access camera: ' + error.message);
    }
  };

  useEffect(() => {
    if (!showCamera || capturedImage) return;
    if (!cameraStream || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = cameraStream;
    video.muted = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('autoplay', 'true');
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        setCameraStatus('error');
        setCameraError(err?.message || 'Unable to start video playback');
      });
    }
  }, [showCamera, cameraStream, capturedImage]);

  const runLivenessCheck = async ({ autoStartCamera = true } = {}) => {
    if (livenessChecking) {
      return;
    }

    if (!streamRef.current) {
      if (!autoStartCamera) {
        toast.error('Camera not available');
        return;
      }
      await startCamera();
    }
    if (!streamRef.current) {
      toast.error('Camera not available');
      return;
    }

    if (!window.MediaRecorder) {
      toast.error('Liveness check not supported in this browser');
      return;
    }

    setLivenessChecking(true);
    setLivenessStatus('checking');
    setLivenessScore(null);
    chunksRef.current = [];

    try {
      const recorder = new MediaRecorder(streamRef.current, { mimeType: 'video/webm' });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      recorder.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: 'video/webm' });
          const result = await attendanceService.livenessCheck(blob);
          setLivenessScore(result.score);
          if (result.passed) {
            setLivenessStatus('passed');
            toast.success('Liveness check passed');
          } else {
            setLivenessStatus('failed');
            toast.error('Liveness check failed');
          }
        } catch (error) {
          setLivenessStatus('failed');
          toast.error(error.response?.data?.detail || 'Liveness check failed');
        } finally {
          recorderRef.current = null;
          setLivenessChecking(false);
        }
      };

      recorder.start();
      setTimeout(() => {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      }, 3000);
    } catch (error) {
      setLivenessChecking(false);
      setLivenessStatus('failed');
      toast.error('Failed to run liveness check');
    }
  };

  const handleRetakePhoto = () => {
    cancelLivenessCheck();
    resetLivenessState();
    setCapturedImage(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    const doCapture = () => {
      if (!video.videoWidth || !video.videoHeight) {
        toast.error('Camera not ready yet. Please try again.');
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      resetLivenessState();
      if (videoRef.current) {
        videoRef.current.pause();
      }
      if (attendanceType !== 'remote') {
        void runLivenessCheck({ autoStartCamera: false });
      }
    };

    if (video.readyState >= 2) {
      doCapture();
    } else {
      video.onloadeddata = () => doCapture();
    }
  };

  const handleCheckIn = async () => {
    if (!currentLocation) {
      toast.error('Please enable location access');
      return;
    }

    if (!capturedImage && attendanceType !== 'remote') {
      toast.error('Please capture a selfie');
      return;
    }

    if (attendanceType !== 'remote' && livenessStatus !== 'passed') {
      toast.error('Please complete liveness check');
      return;
    }

    setLoading(true);
    try {
      const response = await attendanceService.checkIn({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        attendance_type: attendanceType,
        selfie_image: capturedImage,
        device_info: navigator.userAgent,
        notes,
        liveness_score: livenessScore,
        liveness_passed: livenessStatus === 'passed',
      });

        toast.success('Checked in successfully!');
        setTodayStatus(response);
        setCapturedImage(null);
        setNotes('');
        resetLivenessState();
        loadAttendanceData();
      } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Outside allowed location geofence') {
        toast.error('You are outside your allowed work locations');
      } else if (detail === 'No allowed locations assigned to this employee') {
        toast.error('No work locations assigned. Please contact HR.');
      } else {
        toast.error(detail || 'Check-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!currentLocation) {
      getCurrentLocation();
      toast.error('Please wait for location access');
      return;
    }

    setLoading(true);
    try {
      const response = await attendanceService.checkOut({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        selfie_image: capturedImage,
        notes,
      });

      toast.success('Checked out successfully!');
      setTodayStatus(response);
      setCapturedImage(null);
      setNotes('');
      loadAttendanceData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (record) => {
    if (!record.id) {
      toast.error('Cannot edit record: Missing ID');
      console.error('Attendance record is missing ID:', record);
      return;
    }
    setEditingRecord(record);
    setEditForm({
      status: record.status,
      check_in_time: record.check_in_time ? format(new Date(record.check_in_time), "yyyy-MM-dd'T'HH:mm") : '',
      check_out_time: record.check_out_time ? format(new Date(record.check_out_time), "yyyy-MM-dd'T'HH:mm") : '',
      notes: record.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        check_in: editForm.check_in_time || null,
        check_out: editForm.check_out_time || null,
        status: editForm.status,
        reason: editForm.notes
      };
      await attendanceService.edit(editingRecord.id, payload);
      toast.success('Attendance updated successfully');
      setShowEditDialog(false);
      loadAttendanceData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail :
        Array.isArray(detail) ? detail[0]?.msg :
          'Failed to update attendance';
      toast.error(message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      checked_in: { label: 'Checked In', className: 'badge-info' },
      checked_out: { label: 'Checked Out', className: 'badge-success' },
      absent: { label: 'Absent', className: 'badge-danger' },
      on_leave: { label: 'On Leave', className: 'badge-warning' },
      half_day: { label: 'Half Day', className: 'badge-warning' },
    };
    const config = statusConfig[status] || { label: status, className: 'badge-info' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
        <p className="text-muted-foreground mt-1">
          Mark your attendance and view history
        </p>
      </div>

      <Tabs defaultValue="mark" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="mark" data-testid="tab-mark-attendance">Mark Attendance</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar View</TabsTrigger>
        </TabsList>

        {/* Mark Attendance Tab */}
        <TabsContent value="mark" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Card */}
            <Card data-testid="today-status-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Today's Status
                </CardTitle>
                <CardDescription>
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {todayStatus ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Status</span>
                      {getStatusBadge(todayStatus.status)}
                    </div>
                    {todayStatus.check_in_time && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Check In</span>
                        <span className="font-mono">
                          {format(new Date(todayStatus.check_in_time), 'HH:mm:ss')}
                        </span>
                      </div>
                    )}
                    {todayStatus.check_out_time && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Check Out</span>
                        <span className="font-mono">
                          {format(new Date(todayStatus.check_out_time), 'HH:mm:ss')}
                        </span>
                      </div>
                    )}
                    {todayStatus.working_hours > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Working Hours</span>
                        <span className="font-semibold">{todayStatus.working_hours} hrs</span>
                      </div>
                    )}
                    {(todayStatus.location_name || todayStatus.location_id) && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Location</span>
                        <span className="text-right">{todayStatus.location_name || todayStatus.location_id}</span>
                      </div>
                    )}
                    {todayStatus.is_late && (
                      <Badge className="badge-warning">Late Arrival</Badge>
                    )}
                    {!todayStatus.is_within_geofence && (
                      <Badge className="badge-danger">Outside Geofence</Badge>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <XCircle className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Not checked in yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location & Geofence Card */}
            <Card data-testid="location-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Location Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={getCurrentLocation}
                  variant="outline"
                  className="w-full"
                  disabled={loading || locationLoading}
                  data-testid="get-location-btn"
                >
                  {locationLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Navigation className="w-4 h-4 mr-2" />
                  )}
                  {locationLoading ? 'Getting GPS Fix...' : 'Get Current Location'}
                </Button>

                {locationError && (
                  <div className="flex items-center gap-2 text-destructive text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    {locationError}
                  </div>
                )}

                {currentLocation && (
                  <div className="space-y-3 p-4 rounded-lg bg-secondary/50">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Latitude</span>
                      <span className="font-mono">{currentLocation.latitude.toFixed(6)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Longitude</span>
                      <span className="font-mono">{currentLocation.longitude.toFixed(6)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Accuracy</span>
                      <span>{Math.round(currentLocation.accuracy)}m</span>
                    </div>
                  </div>
                )}

                {geofenceStatus && (
                  <div className={`p-4 rounded-lg border ${geofenceStatus.is_valid
                    ? 'border-green-500/30 bg-green-500/10'
                    : 'border-red-500/30 bg-red-500/10'
                    }`}>
                    <div className="flex items-center gap-2">
                      {geofenceStatus.is_valid ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium">
                          {geofenceStatus.is_valid ? 'Within Geofence' : 'Outside Geofence'}
                        </p>
                        {geofenceStatus.location_name && (
                          <p className="text-sm text-muted-foreground">
                            {geofenceStatus.location_name}
                            {typeof geofenceStatus.distance_meters === 'number'
                              ? ` (${geofenceStatus.distance_meters}m from center)`
                              : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    {!geofenceStatus.is_valid && geofenceStatus.closest_location_name && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Closest: {geofenceStatus.closest_location_name} — {geofenceStatus.closest_distance_meters}m away (radius {geofenceStatus.closest_radius_meters}m)
                      </p>
                    )}
                  </div>
                )}

                {/* Map Preview */}
                {currentLocation && GOOGLE_MAPS_API_KEY && (
                  <div className="map-container rounded-lg overflow-hidden">
                    <iframe
                      title="Location Map"
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${currentLocation.latitude},${currentLocation.longitude}&zoom=16`}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Camera & Check-in Section */}
          <Card data-testid="checkin-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Selfie Attendance
              </CardTitle>
              <CardDescription>
                Capture your selfie to verify attendance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Attendance Type</Label>
                  <Select value={attendanceType} onValueChange={setAttendanceType}>
                    <SelectTrigger data-testid="attendance-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="store">Store</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="Add any notes..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="attendance-notes"
                  />
                </div>
              </div>

              {/* Camera View */}
              {showCamera && (
                <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-w-md mx-auto">
                  {capturedImage ? (
                    <img
                      src={capturedImage}
                      alt="Captured selfie"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      onLoadedMetadata={() => {
                        setCameraStatus('streaming');
                        const playPromise = videoRef.current?.play();
                        if (playPromise && typeof playPromise.catch === 'function') {
                          playPromise.catch((err) => {
                            setCameraStatus('error');
                            setCameraError(err?.message || 'Unable to start video playback');
                          });
                        }
                      }}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="scanning-overlay" />
                  <div className="scan-line" />
                  <canvas ref={canvasRef} className="hidden" />

                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                    {!capturedImage && (
                      <Button onClick={capturePhoto} className="glow-primary" data-testid="capture-btn">
                        <Scan className="w-4 h-4 mr-2" />
                        Capture
                      </Button>
                    )}
                    {capturedImage && (
                      <Button
                        variant="outline"
                        onClick={handleRetakePhoto}
                        disabled={livenessChecking}
                      >
                        Retake
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => stopCamera('cancel')} disabled={livenessChecking}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {(cameraStatus === 'error' || cameraError) && (
                <div className="text-sm text-destructive text-center">
                  Camera error: {cameraError || 'Unknown error'}
                </div>
              )}


              {/* Captured Image */}
              {capturedImage && (
                <div className="relative rounded-lg overflow-hidden max-w-md mx-auto">
                  <img
                    src={capturedImage}
                    alt="Captured selfie"
                    className="w-full rounded-lg border border-green-500"
                  />
                  <div className="absolute top-2 right-2">
                    <Badge className="badge-success">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Captured
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute bottom-2 right-2"
                    onClick={handleRetakePhoto}
                    disabled={livenessChecking}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Retake
                  </Button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {!showCamera && !capturedImage && (
                  <Button onClick={startCamera} variant="outline" data-testid="start-camera-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Open Camera
                  </Button>
                )}

                {attendanceType !== 'remote' && capturedImage && (
                  <Button
                    onClick={() => runLivenessCheck({ autoStartCamera: false })}
                    variant="outline"
                    disabled={livenessChecking}
                    data-testid="liveness-check-btn"
                  >
                    {livenessChecking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    {livenessChecking ? 'Checking Liveness...' : 'Run Liveness Again'}
                  </Button>
                )}

                {!todayStatus?.check_in_time && (
                  <Button
                    onClick={handleCheckIn}
                    disabled={loading || !currentLocation || livenessChecking || (attendanceType !== 'remote' && livenessStatus !== 'passed')}
                    className="glow-primary"
                    data-testid="check-in-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                    Check In
                  </Button>
                )}

                {todayStatus?.check_in_time && !todayStatus?.check_out_time && (
                  <Button
                    onClick={handleCheckOut}
                    disabled={loading || !currentLocation}
                    variant="destructive"
                    data-testid="check-out-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogOut className="w-4 h-4 mr-2" />}
                    Check Out
                  </Button>
                )}

                {todayStatus?.check_out_time && (
                  <Badge className="badge-success px-4 py-2">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Attendance Complete
                  </Badge>
                )}
              </div>

              {attendanceType !== 'remote' && livenessStatus !== 'idle' && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Badge className={livenessStatus === 'passed' ? 'badge-success' : livenessStatus === 'failed' ? 'badge-danger' : 'badge-warning'}>
                    {livenessStatus === 'passed' && 'Liveness Passed'}
                    {livenessStatus === 'failed' && 'Liveness Failed'}
                    {livenessStatus === 'checking' && 'Liveness Checking'}
                  </Badge>
                  {typeof livenessScore === 'number' && (
                    <span className="text-muted-foreground">Score: {livenessScore.toFixed(2)}</span>
                  )}
                </div>
              )}
              {capturedImage && attendanceType !== 'remote' && (
                <p className="text-center text-xs text-muted-foreground">
                  Liveness check starts automatically after each selfie capture.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex gap-2">
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-32" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {format(new Date(2024, i, 1), 'MMMM')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-24" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(isAdmin() || isManager()) && (
                <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                  <SelectTrigger className="w-48">
                    <Users className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Select Employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">My Attendance</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Button variant="outline" onClick={loadAttendanceData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table data-testid="attendance-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Check In</TableHead>
                    <TableHead>Check Out</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Working Hours</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendanceHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No attendance records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    attendanceHistory.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">
                          {format(new Date(record.date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell className="font-mono">
                          {record.check_in_time
                            ? format(new Date(record.check_in_time), 'HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {record.check_out_time
                            ? format(new Date(record.check_out_time), 'HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell>{record.location_name || record.location_id || '-'}</TableCell>
                        <TableCell>{record.working_hours || 0} hrs</TableCell>
                        <TableCell className="capitalize">{record.attendance_type}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-between">
                            {getStatusBadge(record.status)}
                            {(isAdmin() || isManager()) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(record)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <AttendanceCalendar
                attendanceHistory={attendanceHistory}
                month={selectedMonth}
                year={selectedYear}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Attendance Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Attendance Record</DialogTitle>
            <DialogDescription>
              Manually adjust check-in/out times and status.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="checked_in">Checked In</SelectItem>
                  <SelectItem value="checked_out">Checked Out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Check In Time</Label>
                <Input
                  type="datetime-local"
                  value={editForm.check_in_time}
                  onChange={(e) => setEditForm({ ...editForm, check_in_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Check Out Time</Label>
                <Input
                  type="datetime-local"
                  value={editForm.check_out_time}
                  onChange={(e) => setEditForm({ ...editForm, check_out_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Admin Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Reason for adjustment..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Attendance Calendar Component
const AttendanceCalendar = ({ attendanceHistory, month, year }) => {
  const attendanceMap = {};
  attendanceHistory.forEach(record => {
    attendanceMap[record.date] = record;
  });

  const getDayClass = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const record = attendanceMap[dateStr];

    if (!record) return '';
    if (record.status === 'checked_out' || record.status === 'checked_in') {
      return record.is_late ? 'calendar-day-late' : 'calendar-day-present';
    }
    if (record.status === 'on_leave') return 'calendar-day-leave';
    if (record.status === 'absent') return 'calendar-day-absent';
    return '';
  };

  return (
    <div className="flex flex-col items-center">
      <Calendar
        mode="single"
        month={new Date(year, month - 1)}
        className="rounded-md border"
        modifiers={{
          present: (date) => {
            const record = attendanceMap[format(date, 'yyyy-MM-dd')];
            return record && ['checked_in', 'checked_out'].includes(record.status) && !record.is_late;
          },
          late: (date) => {
            const record = attendanceMap[format(date, 'yyyy-MM-dd')];
            return record?.is_late;
          },
          leave: (date) => {
            const record = attendanceMap[format(date, 'yyyy-MM-dd')];
            return record?.status === 'on_leave';
          },
          absent: (date) => {
            const record = attendanceMap[format(date, 'yyyy-MM-dd')];
            return record?.status === 'absent';
          },
        }}
        modifiersClassNames={{
          present: 'bg-green-500/20 text-green-400',
          late: 'bg-orange-500/20 text-orange-400',
          leave: 'bg-yellow-500/20 text-yellow-400',
          absent: 'bg-red-500/20 text-red-400',
        }}
      />

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-6 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/20 border border-green-500/50" />
          <span className="text-sm">Present</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-500/20 border border-orange-500/50" />
          <span className="text-sm">Late</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500/20 border border-yellow-500/50" />
          <span className="text-sm">On Leave</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500/20 border border-red-500/50" />
          <span className="text-sm">Absent</span>
        </div>
      </div>
    </div>
  );
};
